import { useMemo } from "react";

// Components
import { Box, ListItemText, Typography, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { RadioButton } from "components/RadioButton";

// Hooks
import { useAuth } from "utils/hooks";

// Icons
import icons from "enums/icons";
import CustomIcon from "components/CustomIcon";

// Styles
import userRoleSelectStyles from "./UserRoleSelect.styles";

// Ability
import { AbilityContext } from "context/Ability";
import { useAbility } from "@casl/react";

// Types
import { TUserRole } from "types/global";

type BasicUserRoleData = {
    id: number;
    function: string | "placeholder" | null | undefined;
};

const UserRoleSelect = ({
    data,
    list,
    onRoleSelection,
    disabled = false,
}: {
    data: BasicUserRoleData;
    list?: TUserRole[];
    onRoleSelection: (event: SelectChangeEvent, userId: number) => void;
    disabled?: boolean;
}) => {
    const classes = userRoleSelectStyles();
    const { loggedInUserData } = useAuth();

    const ability = useAbility(AbilityContext);
    const canEditUserRole = ability.can("see", "SYS_ADMIN_USER_GROUP_ROLES_EDIT");
    const isLoggedInUser = loggedInUserData.userId === data.id;

    const listWithoutPlaceholder = useMemo(() => list?.filter((role) => role.id !== "placeholder"), [list]);
    const selectedValue = useMemo(
        () => listWithoutPlaceholder?.find((role) => role.id === data.function)?.label ?? "",
        [listWithoutPlaceholder, data.function]
    );

    const SelectItem = ({ role }: { role: TUserRole }) => {
        const isSelectedRole = role.label === selectedValue;

        return (
            <Box display="flex" alignItems="center">
                <RadioButton checked={isSelectedRole} />
                <ListItemText primary={<Box display="flex">{role.label}</Box>} secondary={role?.description} />
            </Box>
        );
    };

    return (
        <Select
            id={`role-${data.id}`}
            labelId={`role-${data.id}`}
            name="role-select"
            className={classes.selectField}
            onClick={(event) => event.stopPropagation()}
            value={selectedValue}
            disabled={disabled || !canEditUserRole || isLoggedInUser}
            onChange={(event: SelectChangeEvent) => onRoleSelection(event, data.id)}
            renderValue={(selected) => {
                const role = listWithoutPlaceholder?.find((role) => role.label === selected);
                const isPlaceholder = selected === "";
                return (
                    <Typography
                        sx={(theme) => ({
                            color: isPlaceholder ? theme.palette.text.disabled : theme.palette.text.primary,
                        })}
                    >
                        {role?.label}
                    </Typography>
                );
            }}
            IconComponent={(props) => <CustomIcon icon={icons.chevronDownLight} {...props} sx={{ fontSize: 12 }} />}
            MenuProps={{
                PaperProps: {
                    className: classes.filterByRolePaper,
                },
            }}
        >
            {listWithoutPlaceholder?.map((role) => (
                <MenuItem key={role.id} value={role.label} className={classes.roleSelectMenuItem}>
                    <SelectItem role={role} />
                </MenuItem>
            ))}
        </Select>
    );
};

export default UserRoleSelect;
